/**
 * Wave-4 / A8 — Engine routing.
 *
 * Pure consumer-side helper that decides which 3D engine should render a
 * gTwin scene-package. See `docs/design-briefs/A8-engine-routing-fallback.md`
 * for the state matrix.
 *
 * Decision summary:
 *   - Any non-pointcloud realityData / engineeringModels / etc. item present
 *     -> Giro3D (mixed-scene primary), all items.
 *   - Pure point-cloud scene with `extensions.pointcloud.defaultMode ===
 *     "point-cloud-only"` AND `options.potreeStatus === "ready"` -> Potree,
 *     `potree-v2-octree` items only; alternate is Giro3D for `copc` items in
 *     the same `sourceGroupId`.
 *   - Pure point-cloud scene with Potree NOT ready -> Giro3D, COPC items if
 *     present, reason includes `"potree-not-installed"`.
 *   - Otherwise -> Giro3D default.
 *
 * Reads ONLY existing scene-package fields. G1 — no scene-package shape
 * change. G7 — pure function, no side effects.
 */

import type { SceneSlot } from '@/sdk-stub/gtwin-sdk';
import type { ScenePackageDocument } from './scenePackage';

export type RoutedEngineId = 'giro3d' | 'potree';

/**
 * Status surface the routing helper accepts for the Potree adapter. Matches
 * `PotreeAdapter.PotreeBackendStatus` so the call-site can pass the snapshot
 * through unchanged. Treated as an open enum here so downstream changes to
 * the adapter shape do not require updates in the routing helper.
 */
export type PotreeBackendStatusInput =
  | 'not-installed'
  | 'loading'
  | 'ready'
  | 'failed-to-load'
  | 'failed-during-render';

export interface EngineRoutingOptions {
  /** `'ready'` is the only value that allows automatic Potree selection. */
  readonly potreeStatus?: PotreeBackendStatusInput;
}

export interface EngineSelection {
  readonly engine: RoutedEngineId;
  /** Stable, machine-readable reason hint (e.g. `"mixed-scene"`, `"potree-not-installed"`). */
  readonly reason: string;
  /** Scene-item ids to render on the chosen engine; defaultSceneItemId surfaces first. */
  readonly sceneItemIds: string[];
  /** When present, the engine the fallback control should propose. */
  readonly alternateEngine?: RoutedEngineId;
  /** Scene-item ids the alternate engine would render. */
  readonly alternateSceneItemIds?: string[];
}

type JsonObject = Record<string, unknown>;

/** Layer kinds that can never represent a point cloud, regardless of slot. */
const NON_POINTCLOUD_LAYER_KINDS = new Set<string>([
  'raster',
  'vector',
  'terrain',
  'bim',
  '3dtiles',
  'panorama',
  'panorama-bubble',
  'timeseries',
]);

interface NormalizedItem {
  readonly id: string;
  readonly slot: SceneSlot;
  readonly layerKind: string;
  readonly artifactKind: string | null;
  readonly sourceGroupId: string | null;
  readonly renderRole: string | null;
  readonly defaultSceneItemId: string | null;
  readonly alternateSceneItemIds: ReadonlyArray<string>;
  readonly isPointcloud: boolean;
  readonly isPotreeV2: boolean;
  readonly isCopc: boolean;
}

/**
 * Top-level entry point. Returns a deterministic `EngineSelection`. The
 * function is pure — calling it twice with the same inputs yields the same
 * result (G8 mode-persistence guarantee).
 */
export function selectEngineForScene(
  scenePackage: ScenePackageDocument | null | undefined,
  options: EngineRoutingOptions = {},
): EngineSelection {
  const items = collectItems(scenePackage);

  if (items.length === 0) {
    return {
      engine: 'giro3d',
      reason: 'no-scene-items',
      sceneItemIds: [],
    };
  }

  const allIds = items.map((item) => item.id);

  if (items.some((item) => !item.isPointcloud)) {
    return {
      engine: 'giro3d',
      reason: 'mixed-scene',
      sceneItemIds: orderByDefault(allIds, readPackageDefault(scenePackage)),
    };
  }

  const defaultMode = readPackageDefaultMode(scenePackage);
  const packageDefault = readPackageDefault(scenePackage);
  const potreeStatus = options.potreeStatus ?? 'not-installed';

  if (defaultMode === 'point-cloud-only' && potreeStatus === 'ready') {
    const potreeIds = items.filter((item) => item.isPotreeV2).map((item) => item.id);
    if (potreeIds.length > 0) {
      const copcIds = items.filter((item) => item.isCopc).map((item) => item.id);
      const alternateIds = mergeAlternates(copcIds, items, potreeIds);
      const selection: EngineSelection = {
        engine: 'potree',
        reason: 'point-cloud-only-potree-ready',
        sceneItemIds: orderByDefault(potreeIds, packageDefault),
        ...(alternateIds.length > 0
          ? {
              alternateEngine: 'giro3d' as const,
              alternateSceneItemIds: alternateIds,
            }
          : {}),
      };
      return selection;
    }
    // No Potree-v2 artifact present even though defaultMode asks for it —
    // fall back to Giro3D and surface a precise reason so callers can warn.
    return {
      engine: 'giro3d',
      reason: 'point-cloud-only-no-potree-artifact',
      sceneItemIds: orderByDefault(
        items.filter((item) => item.isCopc).map((item) => item.id),
        packageDefault,
      ),
    };
  }

  if (defaultMode === 'point-cloud-only') {
    // Potree requested but adapter not ready.
    const copcIds = items.filter((item) => item.isCopc).map((item) => item.id);
    const ids = copcIds.length > 0 ? copcIds : allIds;
    return {
      engine: 'giro3d',
      reason: `point-cloud-only-potree-not-installed:${potreeStatus}`,
      sceneItemIds: orderByDefault(ids, packageDefault),
    };
  }

  // Pure point-cloud scene but no explicit Potree request — conservative
  // default is Giro3D (the unified mixed-scene engine).
  return {
    engine: 'giro3d',
    reason: 'point-cloud-only-default-giro3d',
    sceneItemIds: orderByDefault(allIds, packageDefault),
  };
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function collectItems(scenePackage: ScenePackageDocument | null | undefined): NormalizedItem[] {
  if (!scenePackage || !isObject(scenePackage.slots)) return [];
  const result: NormalizedItem[] = [];
  const slots = scenePackage.slots as Record<string, unknown>;
  for (const slotName of Object.keys(slots)) {
    const slot = slotName as SceneSlot;
    const raw = slots[slotName];
    const items = Array.isArray(raw) ? raw : raw && typeof raw === 'object' ? [raw] : [];
    for (const entry of items) {
      if (!isObject(entry)) continue;
      const normalized = normalizeItem(entry, slot);
      if (normalized) result.push(normalized);
    }
  }
  return result;
}

function normalizeItem(raw: JsonObject, slot: SceneSlot): NormalizedItem | null {
  const id = typeof raw.id === 'string' && raw.id.length > 0 ? raw.id : null;
  if (!id) return null;
  const layerKind = typeof raw.layerKind === 'string' ? raw.layerKind : '';
  const artifactKind = typeof raw.artifactKind === 'string' ? raw.artifactKind : null;
  const extensions = isObject(raw.extensions) ? raw.extensions : undefined;
  const pointcloudExt = extensions && isObject(extensions.pointcloud) ? extensions.pointcloud : undefined;

  const sourceGroupId =
    pointcloudExt && typeof pointcloudExt.sourceGroupId === 'string' && pointcloudExt.sourceGroupId.length > 0
      ? pointcloudExt.sourceGroupId
      : null;
  const renderRole =
    pointcloudExt && typeof pointcloudExt.renderRole === 'string' ? pointcloudExt.renderRole : null;
  const defaultSceneItemId =
    pointcloudExt && typeof pointcloudExt.defaultSceneItemId === 'string'
      ? pointcloudExt.defaultSceneItemId
      : null;
  const alternateSceneItemIds = pointcloudExt && Array.isArray(pointcloudExt.alternateSceneItemIds)
    ? pointcloudExt.alternateSceneItemIds.filter((value): value is string => typeof value === 'string')
    : [];

  const isPotreeV2 = artifactKind === 'potree-v2-octree';
  const isCopc = artifactKind === 'copc';
  const is3dTilesPointcloud = artifactKind === '3dtiles-pointcloud';
  const isPointcloud =
    (layerKind === 'pointcloud' || isPotreeV2 || isCopc || is3dTilesPointcloud)
    && !NON_POINTCLOUD_LAYER_KINDS.has(layerKind);

  return {
    id,
    slot,
    layerKind,
    artifactKind,
    sourceGroupId,
    renderRole,
    defaultSceneItemId,
    alternateSceneItemIds,
    isPointcloud,
    isPotreeV2,
    isCopc,
  };
}

function readPackageExtensions(scenePackage: ScenePackageDocument | null | undefined): JsonObject | null {
  if (!scenePackage) return null;
  const ext = (scenePackage as { extensions?: unknown }).extensions;
  return isObject(ext) ? ext : null;
}

function readPackagePointcloudExtensions(scenePackage: ScenePackageDocument | null | undefined): JsonObject | null {
  const ext = readPackageExtensions(scenePackage);
  if (!ext) return null;
  return isObject(ext.pointcloud) ? ext.pointcloud : null;
}

function readPackageDefaultMode(scenePackage: ScenePackageDocument | null | undefined): string | null {
  const ext = readPackagePointcloudExtensions(scenePackage);
  if (!ext) return null;
  return typeof ext.defaultMode === 'string' ? ext.defaultMode : null;
}

function readPackageDefault(scenePackage: ScenePackageDocument | null | undefined): string | null {
  const ext = readPackagePointcloudExtensions(scenePackage);
  if (!ext) return null;
  return typeof ext.defaultSceneItemId === 'string' ? ext.defaultSceneItemId : null;
}

/**
 * Put `defaultSceneItemId` first when present. Stable across repeated calls.
 */
function orderByDefault(ids: ReadonlyArray<string>, packageDefault: string | null): string[] {
  if (!packageDefault) return [...ids];
  if (!ids.includes(packageDefault)) return [...ids];
  return [packageDefault, ...ids.filter((id) => id !== packageDefault)];
}

/**
 * Merge alternate ids: start with COPC ids (Giro3D's natural alternate for a
 * Potree-led scene), then append any `alternateSceneItemIds` that are not
 * already covered AND not nominated as the Potree-side default.
 */
function mergeAlternates(
  copcIds: ReadonlyArray<string>,
  items: ReadonlyArray<NormalizedItem>,
  potreeIds: ReadonlyArray<string>,
): string[] {
  const merged = new Set<string>(copcIds);
  const exclude = new Set<string>(potreeIds);
  for (const item of items) {
    if (!item.isPotreeV2) continue;
    for (const alt of item.alternateSceneItemIds) {
      if (!exclude.has(alt)) merged.add(alt);
    }
  }
  return [...merged];
}

function isObject(value: unknown): value is JsonObject {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
